import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import DocumentUpload from './components/DocumentUpload';
import DocumentList from './components/DocumentList';
import DocumentViewer from './components/DocumentViewer';

export interface Document {
  id: string;
  name: string;
  category: string;
  uploadedBy: string;
  uploadDate: string;
  size: string;
  type: string;
  url: string;
  description?: string;
  version: string;
  tags: string[];
}

export default function Documents() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([
    {
      id: '1',
      name: 'Q4 2023 Financial Report',
      category: 'Financial',
      uploadedBy: 'Kalyan',
      uploadDate: '2024-01-15',
      size: '2.4 MB',
      type: 'PDF',
      url: 'https://readdy.ai/api/search-image?query=professional%20financial%20report%20document%20with%20clean%20layout%20showing%20charts%20and%20tables%20on%20white%20background%20business%20style%20modern%20design%20high%20quality%20detailed%20presentation&width=800&height=1000&seq=doc1&orientation=portrait',
      description: 'Quarterly financial performance and analysis',
      version: '1.0',
      tags: ['finance', 'quarterly', 'report']
    },
    {
      id: '2',
      name: 'Employee Handbook 2024',
      category: 'HR',
      uploadedBy: 'Shirish',
      uploadDate: '2024-01-10',
      size: '5.1 MB',
      type: 'PDF',
      url: 'https://readdy.ai/api/search-image?query=professional%20employee%20handbook%20cover%20page%20with%20corporate%20design%20clean%20white%20background%20modern%20business%20document%20style%20high%20quality%20detailed%20layout&width=800&height=1000&seq=doc2&orientation=portrait',
      description: 'Complete guide for all employees',
      version: '2.0',
      tags: ['hr', 'policies', 'handbook']
    },
    {
      id: '3',
      name: 'Moringa Product Specifications',
      category: 'Product',
      uploadedBy: 'Amol',
      uploadDate: '2024-01-08',
      size: '1.8 MB',
      type: 'PDF',
      url: 'https://readdy.ai/api/search-image?query=product%20specification%20document%20with%20moringa%20leaves%20and%20powder%20images%20clean%20white%20background%20professional%20layout%20modern%20design%20high%20quality%20detailed&width=800&height=1000&seq=doc3&orientation=portrait',
      description: 'Technical specifications and quality standards',
      version: '1.2',
      tags: ['product', 'specifications', 'moringa']
    },
    {
      id: '4',
      name: 'Farming Activity Guidelines',
      category: 'Operations',
      uploadedBy: 'Kalyan',
      uploadDate: '2024-01-05',
      size: '3.2 MB',
      type: 'PDF',
      url: 'https://readdy.ai/api/search-image?query=farming%20guidelines%20document%20with%20agricultural%20illustrations%20clean%20white%20background%20professional%20layout%20modern%20design%20high%20quality%20detailed%20presentation&width=800&height=1000&seq=doc4&orientation=portrait',
      description: 'Best practices for farming operations',
      version: '1.0',
      tags: ['farming', 'operations', 'guidelines']
    },
    {
      id: '5',
      name: 'Marketing Strategy 2024',
      category: 'Marketing',
      uploadedBy: 'Shirish',
      uploadDate: '2024-01-03',
      size: '4.5 MB',
      type: 'PDF',
      url: 'https://readdy.ai/api/search-image?query=marketing%20strategy%20presentation%20document%20with%20charts%20and%20graphs%20clean%20white%20background%20professional%20business%20style%20modern%20design%20high%20quality&width=800&height=1000&seq=doc5&orientation=portrait',
      description: 'Annual marketing plan and strategies',
      version: '1.0',
      tags: ['marketing', 'strategy', 'planning']
    },
    {
      id: '6',
      name: 'Safety Protocols',
      category: 'Safety',
      uploadedBy: 'Amol',
      uploadDate: '2023-12-28',
      size: '2.1 MB',
      type: 'PDF',
      url: 'https://readdy.ai/api/search-image?query=safety%20protocols%20document%20with%20warning%20symbols%20and%20guidelines%20clean%20white%20background%20professional%20layout%20modern%20design%20high%20quality%20detailed&width=800&height=1000&seq=doc6&orientation=portrait',
      description: 'Workplace safety guidelines and procedures',
      version: '2.1',
      tags: ['safety', 'protocols', 'workplace']
    }
  ]);

  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Financial', 'HR', 'Product', 'Operations', 'Marketing', 'Safety', 'Legal', 'Technical'];

  const handleUpload = (newDocument: Omit<Document, 'id' | 'uploadDate' | 'uploadedBy'>) => {
    const currentUser = localStorage.getItem('userName') || 'User';
    const document: Document = {
      ...newDocument,
      id: Date.now().toString(),
      uploadDate: new Date().toISOString().split('T')[0],
      uploadedBy: currentUser
    };
    setDocuments([document, ...documents]);
    setShowUploadModal(false);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this document?')) {
      setDocuments(documents.filter(doc => doc.id !== id));
      if (selectedDocument?.id === id) {
        setSelectedDocument(null);
      }
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <DashboardHeader />
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Document Management</h1>
                <p className="text-sm text-gray-600 mt-1">Upload, organize, and share company documents</p>
              </div>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-upload-cloud-line text-xl w-5 h-5 flex items-center justify-center"></i>
                Upload Document
              </button>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 relative">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                  <input
                    type="text"
                    placeholder="Search documents by name, description, or tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <DocumentList
                  documents={filteredDocuments}
                  onView={setSelectedDocument}
                  onDelete={handleDelete}
                />
              </div>
              <div className="lg:col-span-1">
                <DocumentViewer document={selectedDocument} />
              </div>
            </div>
          </div>
        </main>
      </div>

      {showUploadModal && (
        <DocumentUpload
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUpload}
          categories={categories.filter(c => c !== 'All')}
        />
      )}
    </div>
  );
}
